import { asMaybe } from '@execonline-inc/error-handling';
import { assertNever } from '@kofno/piper';
import { dateISO } from 'jsonous';
import { Maybe, fromNullable, just, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { FlashAlert, errorAlert } from '../../../../../Notifications/Types';
import { ParticipantResource } from '../../../../../ParticipantsStore/Types';
import { TPlainTextKey } from '../../../../../Translations';
import ModalStore from '../../../../Modal/Store';
import { GroupParticipantsResource } from '../../../../People/Group/GroupParticipantsStore/Types';
import {
  ParticipantsInvitedResource,
  ProductLicenseInformationResource,
  State,
  addNewGroup,
  addNewParticipant,
  back,
  error,
  getLicenseInformation,
  getLicenseInformationWithNewGroup,
  getLicenseInformationWithNewParticipant,
  inviting,
  licenseInformationWithRemovedGroup,
  licenseInformationWithRemovedParticipant,
  participantsInvited,
  readyWithLicenseInformation,
  readyWithNewGroup,
  readyWithNewParticipant,
  readyWithRemovedGroup,
  readyWithRemovedParticipant,
  removeGroup,
  removeParticipant,
  setDate,
  setSkipCommunications,
  setTime,
  setTimezone,
  submit,
} from './Types';
import { FilterOption } from '../../ParticipantDynamicTypeaheadSelect/UseParticipantDynamicTypeaheadSelect/Types';

export const parseDate = (dateString: string): Maybe<Date> =>
  asMaybe<string, Date>(dateISO.decodeAny(dateString));

const toDate = (time: string): Date => {
  let now = new Date();
  now.setHours(Number(time.slice(0, 2)));
  now.setMinutes(Number(time.slice(3, 5)));
  now.setSeconds(0);
  return now;
};

class InviteParticipantStore {
  @observable
  state: State;

  constructor() {
    makeObservable(this);
    this.state = getLicenseInformation();
  }
  readonly modalStore = new ModalStore();

  @action
  back = () => {
    switch (this.state.kind) {
      case 'inviting':
        this.state = back(this.state);
        break;
      case 'new-participant':
      case 'new-group':
      case 'ready':
      case 'remove-participant':
      case 'remove-group':
      case 'error':
      case 'submitting':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  readyWithLicenseInformation = (productLicenseInformation: ProductLicenseInformationResource) => {
    switch (this.state.kind) {
      case 'get-license-information':
        this.state = readyWithLicenseInformation(this.state, productLicenseInformation);
        break;
      case 'new-group':
      case 'new-participant':
      case 'ready':
      case 'remove-participant':
      case 'remove-group':
      case 'error':
      case 'submitting':
      case 'inviting':
      case 'participants-invited':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  getLicenseInformationWithNewParticipant = (participantResource: ParticipantResource) => {
    switch (this.state.kind) {
      case 'new-participant':
        this.state = getLicenseInformationWithNewParticipant(this.state, participantResource);
        break;
      case 'new-group':
      case 'ready':
      case 'remove-participant':
      case 'remove-group':
      case 'error':
      case 'submitting':
      case 'inviting':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  getLicenseInformation = () => {
    this.state = getLicenseInformation();
  };

  @action
  getLicenseInformationWithNewGroup = (groupResource: GroupParticipantsResource) => {
    switch (this.state.kind) {
      case 'new-group':
        this.state = getLicenseInformationWithNewGroup(this.state, groupResource);
        break;
      case 'new-participant':
      case 'ready':
      case 'remove-participant':
      case 'remove-group':
      case 'error':
      case 'inviting':
      case 'submitting':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  licenseInformationWithRemovedParticipant = () => {
    switch (this.state.kind) {
      case 'remove-participant':
        this.state = licenseInformationWithRemovedParticipant(this.state);
        break;
      case 'inviting':
      case 'remove-group':
      case 'ready':
      case 'new-participant':
      case 'new-group':
      case 'submitting':
      case 'error':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  licenseInformationWithRemovedGroup = () => {
    switch (this.state.kind) {
      case 'remove-group':
        this.state = licenseInformationWithRemovedGroup(this.state);
        break;
      case 'inviting':
      case 'remove-participant':
      case 'ready':
      case 'new-participant':
      case 'new-group':
      case 'submitting':
      case 'error':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  readyWithNewParticipant = (participantResource: ParticipantResource) => {
    switch (this.state.kind) {
      case 'new-participant':
        this.state = readyWithNewParticipant(this.state, participantResource);
        break;
      case 'new-group':
      case 'ready':
      case 'remove-participant':
      case 'remove-group':
      case 'error':
      case 'submitting':
      case 'inviting':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  readyWithNewGroup = (groupResource: GroupParticipantsResource) => {
    switch (this.state.kind) {
      case 'new-group':
        this.state = readyWithNewGroup(this.state, groupResource);
        break;
      case 'new-participant':
      case 'ready':
      case 'remove-participant':
      case 'remove-group':
      case 'error':
      case 'inviting':
      case 'submitting':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  addParticipant = (option: FilterOption) => {
    switch (this.state.kind) {
      case 'ready':
      case 'error':
        this.state = addNewParticipant(this.state, option.payload.value);
        break;
      case 'new-participant':
      case 'new-group':
      case 'remove-participant':
      case 'remove-group':
      case 'inviting':
      case 'submitting':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  setDate = (date: string) => {
    switch (this.state.kind) {
      case 'inviting':
        const state = this.state;
        fromNullable(date)
          .andThen(parseDate)
          .orElse(() => just(new Date()))
          .do((date) => (this.state = setDate(state, date)));
        break;
      case 'ready':
      case 'new-participant':
      case 'new-group':
      case 'remove-participant':
      case 'remove-group':
      case 'submitting':
      case 'error':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  setTime = (date: string) => {
    switch (this.state.kind) {
      case 'inviting':
        const state = this.state;
        fromNullable(date)
          .map(toDate)
          .orElse(() => just(new Date()))
          .do((date) => {
            this.state = setTime(state, date);
          });
        break;
      case 'ready':
      case 'new-participant':
      case 'new-group':
      case 'remove-participant':
      case 'remove-group':
      case 'submitting':
      case 'error':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  setTimezone = (timezone: string) => {
    switch (this.state.kind) {
      case 'inviting':
        const state = this.state;
        fromNullable(timezone).do((timezone) => {
          this.state = setTimezone(state, timezone);
        });
        break;
      case 'ready':
      case 'new-participant':
      case 'new-group':
      case 'remove-participant':
      case 'remove-group':
      case 'submitting':
      case 'error':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  setSkipCommunications = (skip: boolean) => {
    switch (this.state.kind) {
      case 'inviting':
        this.state = setSkipCommunications(this.state, skip);
        break;
      case 'ready':
      case 'new-participant':
      case 'new-group':
      case 'remove-participant':
      case 'remove-group':
      case 'submitting':
      case 'error':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  addGroup = (option: FilterOption) => {
    switch (this.state.kind) {
      case 'ready':
      case 'error':
        this.state = addNewGroup(this.state, option.payload.value);
        break;
      case 'new-participant':
      case 'remove-group':
      case 'new-group':
      case 'remove-participant':
      case 'submitting':
      case 'inviting':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  removeParticipant = (id: number) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = removeParticipant(this.state, id.toString());
        break;
      case 'new-participant':
      case 'new-group':
      case 'remove-participant':
      case 'remove-group':
      case 'submitting':
      case 'inviting':
      case 'error':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  removeGroup = (id: number) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = removeGroup(this.state, id.toString());
        break;
      case 'new-participant':
      case 'new-group':
      case 'remove-participant':
      case 'remove-group':
      case 'submitting':
      case 'inviting':
      case 'error':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  readywithRemovedParticipant = () => {
    switch (this.state.kind) {
      case 'remove-participant':
        this.state = readyWithRemovedParticipant(this.state);
        break;
      case 'inviting':
      case 'remove-group':
      case 'ready':
      case 'new-participant':
      case 'new-group':
      case 'submitting':
      case 'error':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  readywithRemovedGroup = () => {
    switch (this.state.kind) {
      case 'remove-group':
        this.state = readyWithRemovedGroup(this.state);
        break;
      case 'inviting':
      case 'remove-participant':
      case 'ready':
      case 'new-participant':
      case 'new-group':
      case 'submitting':
      case 'error':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  inviting = () => {
    switch (this.state.kind) {
      case 'ready':
      case 'error':
        this.state = inviting(this.state);
        break;
      case 'inviting':
      case 'remove-participant':
      case 'remove-group':
      case 'new-participant':
      case 'new-group':
      case 'submitting':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submit = () => {
    switch (this.state.kind) {
      case 'inviting':
        this.state = submit(this.state);
        break;
      case 'ready':
      case 'remove-participant':
      case 'remove-group':
      case 'new-participant':
      case 'new-group':
      case 'error':
      case 'submitting':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  error = (msg: TPlainTextKey) => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = error(this.state, msg);
        break;
      case 'ready':
      case 'new-participant':
      case 'new-group':
      case 'remove-participant':
      case 'remove-group':
      case 'inviting':
      case 'participants-invited':
      case 'error':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  participantsInvited = (participantsInvitedResource: ParticipantsInvitedResource) => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = participantsInvited(participantsInvitedResource);
        break;
      case 'ready':
      case 'inviting':
      case 'new-participant':
      case 'new-group':
      case 'remove-participant':
      case 'remove-group':
      case 'error':
      case 'participants-invited':
      case 'get-license-information':
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get notification(): Maybe<FlashAlert> {
    switch (this.state.kind) {
      case 'error':
        return just(this.state).map(errorAlert);
      case 'ready':
      case 'new-participant':
      case 'new-group':
      case 'submitting':
      case 'inviting':
      case 'remove-participant':
      case 'remove-group':
      case 'participants-invited':
      case 'get-license-information':
        return nothing();
    }
  }
}

export default InviteParticipantStore;
